/**
 * THE REVIEW SURFACE, SHARED BY EVERY PRODUCT THAT SHOWS SOMEBODY A TRANSACTION.
 *
 * ⚠️ THERE MUST BE EXACTLY ONE OF THESE. Solo and the Org signer view show the same transaction
 * to the same kind of person for the same decision, and if they render it from two code paths
 * those paths drift — one gains a field, one fixes a rounding, one starts labelling change
 * differently. Then two people looking at "the same" payment are looking at two different
 * screens, which is precisely the substitution this product exists to make impossible.
 *
 * So this module is PURE: it takes an intent and a list of findings and returns markup. It reads
 * no application state and touches no DOM. Everything that differs between the two products —
 * where the bytes came from, who is allowed to approve, what happens next — stays in the app.
 *
 * The plain-language layer lives in `packages/core/src/explain.ts` for the same reason: the words
 * a non-technical reader sees must not depend on which surface they happened to open.
 */
import {
  explain, SEVERITY_PLAIN, NETWORK_PLAIN,
  type Finding, type Severity, type Intent, type IntentOutput,
} from '@signingroom/core';
import { html, raw, btc, type Raw } from './dom.js';

/** Severity → the house stylesheet's ramp. */
export const PILL: Record<Severity, string> = {
  BLOCK: 'block', CRITICAL: 'critical', WARN: 'warn',
  INFO: 'info', UNVERIFIABLE: 'unver', PASS: 'ok',
};

/** What the caller knows about this reader's progress through the findings. */
export interface ReviewState {
  /** Codes the reader has explicitly ticked. */
  acknowledged: ReadonlySet<string>;
  /** Codes that REQUIRE a tick before signing. */
  mustAcknowledge: readonly string[];
  /** Codes whose disclosure is currently open, so a re-render does not collapse them. */
  opened: readonly string[];
}

const EMPTY: ReviewState = { acknowledged: new Set(), mustAcknowledge: [], opened: [] };

export const outputRow = (o: IntentOutput): Raw => html`
  <tr>
    <td class="mono">${o.index}</td>
    <td class="mono" style="word-break:break-all">
      ${o.address ?? html`<span class="grey">non-standard script</span>`}
      ${!o.standard ? html`<span class="pill warn" style="margin-left:6px">non-standard</span>` : ''}
    </td>
    <td>
      ${o.role === 'change'
        ? html`<span class="pill ok">back to you</span>`
        : html`<span class="pill navy">someone else</span>`}
    </td>
    <td class="num">${btc(o.amountSats)}</td>
  </tr>`;

export function intentPanel(intent: Intent): Raw {
  const external = intent.outputs.filter((o) => o.role === 'external');
  const sending = external.reduce((a, o) => a + o.amountSats, 0n);

  return html`
    <div class="intent">
      <div class="hd">
        <span class="t">What this payment does</span>
        <span class="t">${NETWORK_PLAIN[intent.network].label}</span>
      </div>
      <div class="bd">
        <p class="label grey">Leaving your wallet</p>
        <p class="big">${btc(sending)} <small>BTC</small></p>
        <p class="sub" style="margin:4px 0 16px">
          ${external.length === 0
            ? 'Nothing leaves. This moves money between your own addresses.'
            : external.length === 1
            ? 'to one address that is not yours'
            : `to ${external.length} addresses that are not yours`}
        </p>

        <dl>
          <div class="dr"><dt>Coins being spent</dt><dd>${
            intent.totalInSats === null
              ? html`<span class="pill unver">not stated in the file</span>`
              : `${btc(intent.totalInSats)} BTC`}</dd></div>
          <div class="dr"><dt>Total paid out</dt><dd>${btc(intent.totalOutSats)} BTC</dd></div>
          <div class="dr"><dt>Network fee</dt><dd>${
            intent.feeSats === null
              ? html`<span class="pill unver">cannot be worked out</span>`
              : `${btc(intent.feeSats)} BTC`}</dd></div>
          <div class="dr"><dt>Fee rate <span class="sub" style="font-size:11px">what miners charge by size</span></dt><dd>${
            intent.feeRate === null
              ? html`<span class="pill unver">unknown</span>`
              : `${intent.feeRate.toFixed(2)} sat/vB · ${intent.vsize} vB est.`}</dd></div>
          <div class="dr"><dt>Fee can be raised later</dt><dd>${
            intent.rbf ? 'yes' : 'no, this cannot be sped up'}</dd></div>
          <div class="dr"><dt>Earliest it can confirm</dt><dd>${
            intent.locktime === 0 ? 'straight away' : `not before ${intent.locktime}`}</dd></div>
        </dl>

        <h4 style="margin:20px 0 8px">Where the money goes</h4>
        <div class="tbl-wrap">
          <table>
            <thead><tr><th>#</th><th>Address</th><th>Who gets it</th><th style="text-align:right">Amount</th></tr></thead>
            <tbody>${intent.outputs.map(outputRow)}</tbody>
          </table>
        </div>

        <h4 style="margin:20px 0 8px">Which coins are being spent</h4>
        <div class="tbl-wrap">
          <table>
            <thead><tr><th>#</th><th>Whose coin</th><th>What the signature covers</th><th style="text-align:right">Amount</th></tr></thead>
            <tbody>${intent.inputs.map((i) => html`
              <tr>
                <td class="mono">${i.index}</td>
                <td>${i.mine === true
                      ? html`<span class="pill ok">yours</span>`
                      : i.mine === false
                      ? html`<span class="pill critical">not yours</span>`
                      : html`<span class="pill unver">could not tell</span>`}</td>
                <td class="mono">${i.sighash === 1
                      ? html`the whole transaction`
                      : html`<span class="pill critical">only part of it</span>`}</td>
                <td class="num">${i.amountSats === null
                      ? html`<span class="pill unver">unknown</span>`
                      : btc(i.amountSats)}</td>
              </tr>`)}
            </tbody>
          </table>
        </div>
      </div>
    </div>`;
}

/**
 * One finding, in plain words first.
 *
 * ⚠️ THE TECHNICAL TEXT IS STILL HERE. It has moved down, not away.
 *
 * The summary line used to be `<code>CHANGE_NOT_MINE</code>` followed by a sentence written for
 * someone who already knows what a change output is. Most people who have to approve a company
 * transaction are a finance lead or a founder, and a check they cannot read is a check they
 * click past. So the order is now: what happened, why it matters, what to do, and then the exact
 * engine output under a disclosure that says it is technical.
 *
 * The disclosure is not decoration. This product's claim is that a signer reads what they are
 * approving, and an auditor has to be able to check a specific statement in place rather than
 * take the friendly version on trust. If the plain sentence and the detail ever disagree, the
 * detail is the truth — it comes straight from the engine and carries the real amounts.
 */
export function findingCard(f: Finding, s: ReviewState = EMPTY): Raw {
  const needsAck = s.mustAcknowledge.includes(f.code);
  const ticked = s.acknowledged.has(f.code);
  const opened = s.opened;
  const e = explain(f.code);
  const sev = SEVERITY_PLAIN[f.severity];
  return html`
    <details class="finding ${PILL[f.severity]}" data-code="${f.code}" ${
      opened.includes(f.code) || f.severity === 'BLOCK' || f.severity === 'CRITICAL'
        ? raw('open') : ''}>
      <summary>
        <span class="pill ${PILL[f.severity]}">${sev.label}</span>
        <span class="ftitle">${e.title}</span>
      </summary>
      <div class="body">
        <p>${e.meaning}</p>
        ${e.whatToDo
          ? html`<p style="margin-top:8px"><strong>What to do:</strong> ${e.whatToDo}</p>`
          : ''}
        ${needsAck
          ? html`<div class="ackbox">
                   <label style="display:flex;gap:9px;align-items:flex-start;cursor:pointer">
                     <input type="checkbox" data-ack="${f.code}" ${ticked ? raw('checked') : ''}>
                     <span>I have read this and accept it.</span>
                   </label>
                 </div>`
          : ''}
        <details class="tech">
          <summary>Technical detail</summary>
          <p class="mono" style="font-size:11.5px;margin-top:8px">${f.code}</p>
          <p style="margin-top:6px">${f.detail}</p>
          <p class="sub" style="margin-top:8px">${sev.label}: ${sev.sense}</p>
        </details>
      </div>
    </details>`;
}


/**
 * Every finding, worst first, with the intent panel above it.
 *
 * The ordering is the specification: a reader who stops after the first card must have seen the
 * most serious thing. Sorting by severity rather than by the order the catalog happened to emit
 * them is what makes that true.
 */
const ORDER: Severity[] = ['BLOCK', 'CRITICAL', 'WARN', 'UNVERIFIABLE', 'INFO', 'PASS'];

export function reviewPanel(
  intent: Intent, findings: readonly Finding[], s: ReviewState = EMPTY,
): Raw {
  const sorted = [...findings].sort(
    (a, b) => ORDER.indexOf(a.severity) - ORDER.indexOf(b.severity));
  return html`
    ${intentPanel(intent)}
    <div style="margin-top:20px">
      <h4>What we found</h4>
      ${sorted.map((f) => findingCard(f, s))}
    </div>`;
}
